import { c as _c } from "react/compiler-runtime";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { AgentRunStatus } from "@/src/models/agent-run";
import { getTaskAgentMessages, getTaskAgentRun } from "@/src/requests/agent-run";
export const TASK_AGENT_RUN_CACHE_KEY = "task-agent-run";
export const TASK_AGENT_MESSAGES_CACHE_KEY = "task-agent-messages";
export const TASK_AGENT_RUN_STALE_TIME_MS = 1000 * 30;
export const TASK_AGENT_RUN_POLL_INTERVAL_MS = 1000 * 5;
const ACTIVE_AGENT_RUN_STATUSES: AgentRunStatus[] = [AgentRunStatus.QUEUED, AgentRunStatus.PROCESSING, AgentRunStatus.MATERIALIZING];
export function isAgentRunStatusActive(status: AgentRunStatus | null | undefined): boolean {
  return status !== null && status !== undefined && ACTIVE_AGENT_RUN_STATUSES.includes(status);
}

// Whether the agent for this task is still actively running
export function isTaskAgentRunActive(queryClient: QueryClient, taskId: string): boolean {
  const data = queryClient.getQueryData<Awaited<ReturnType<typeof getTaskAgentRun>>>([TASK_AGENT_RUN_CACHE_KEY, taskId]);
  const status = data?.agentRun?.status;
  return isAgentRunStatusActive(status);
}
export function useTaskAgentRun(taskId, t0) {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  let t4;
  if ($[2] !== taskId) {
    t3 = [TASK_AGENT_RUN_CACHE_KEY, taskId];
    t4 = () => getTaskAgentRun(taskId);
    $[2] = taskId;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== enabled || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled,
      staleTime: TASK_AGENT_RUN_STALE_TIME_MS,
      refetchInterval: _temp
    };
    $[5] = enabled;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return useQuery(t5);
}
function _temp(query) {
  const status = query.state.data?.agentRun?.status;
  return isAgentRunStatusActive(status) ? TASK_AGENT_RUN_POLL_INTERVAL_MS : false;
}
export function useTaskAgentMessages(taskId, t0) {
  const $ = _c(8);
  const {
    enabled,
    poll
  } = t0;
  let t1;
  let t2;
  if ($[0] !== taskId) {
    t1 = [TASK_AGENT_MESSAGES_CACHE_KEY, taskId];
    t2 = () => getTaskAgentMessages(taskId);
    $[0] = taskId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = poll ? TASK_AGENT_RUN_POLL_INTERVAL_MS : false;
  let t4;
  if ($[3] !== enabled || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled,
      staleTime: TASK_AGENT_RUN_STALE_TIME_MS,
      refetchInterval: t3
    };
    $[3] = enabled;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
}